// AskDeweyAnswer.tsx
// Renders an Ask Dewey answer as formatted markdown, turning references that
// resolve against the live graph into chips that focus the node.
//
// The answer is model output, so `rehype-raw` is deliberately NOT enabled here
// (unlike RoadmapPage, which renders trusted local content).  Raw HTML from an
// LLM response must not be injected into the DOM.

import React, { useCallback, useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getNodeBaseColor } from '../utils/colors';
import {
  extractInlineCodeTokens,
  findSummaryDrift,
  isRelationshipToken,
  removeDuplicateEntityLabels,
  resolveAnswerEntities,
  splitSummary,
} from '../utils/askDeweyEntities';
import type { AnswerEntityIndex } from '../utils/askDeweyEntities';
import type { GraphSearchResultItem, NodeInstance } from '../types';

export interface AskDeweyAnswerProps {
  answer: string;
  /** Nodes currently loaded in the graph — the grounding set for resolution. */
  graphNodes?: readonly NodeInstance[];
  /** Structured backend results; authoritative when populated. */
  results?: readonly GraphSearchResultItem[];
  /** Focus a resolved node. Wired to the same path as keyword search results. */
  onFocusNode?: (node: NodeInstance) => void;
  /** Currently focused node, for active chip styling. */
  focusNodeId?: string | null;
  /** Receives the resolved entity index whenever it changes. */
  onEntitiesResolved?: (index: AnswerEntityIndex) => void;
}

/** Harvests inline-code spans, which is how the agent writes entity references. */

const AskDeweyAnswer: React.FC<AskDeweyAnswerProps> = ({
  answer,
  graphNodes,
  results = [],
  onFocusNode,
  focusNodeId,
  onEntitiesResolved,
}) => {
  const [detailOpen, setDetailOpen] = useState(false);

  const split = useMemo(() => splitSummary(answer), [answer]);

  // Resolution runs over the whole answer, not just the visible summary, so
  // collapsing the detail never shrinks the set highlighted in the graph.
  const index = useMemo(
    () => resolveAnswerEntities(extractInlineCodeTokens(answer), graphNodes, results),
    [answer, graphNodes, results],
  );

  const drift = useMemo(() => (split ? findSummaryDrift(split) : []), [split]);

  const onEntitiesResolvedRef = React.useRef(onEntitiesResolved);
  React.useEffect(() => {
    onEntitiesResolvedRef.current = onEntitiesResolved;
  }, [onEntitiesResolved]);

  React.useEffect(() => {
    onEntitiesResolvedRef.current?.(index);
  }, [index]);

  const renderCode = useCallback(
    (props: React.ComponentPropsWithoutRef<'code'>): React.ReactElement => {
      const { children, className, ...rest } = props;
      const token = String(children).trim();

      // Fenced blocks carry a language class; leave them as plain code.
      if (className) {
        return <code className={className} {...rest}>{children}</code>;
      }

      if (isRelationshipToken(token)) {
        return <code className="ask-dewey-answer__rel" {...rest}>{children}</code>;
      }

      const node = index.byToken.get(token);
      if (!node || !onFocusNode) {
        // Unresolved: render inert so no affordance points nowhere.
        return <code className="ask-dewey-answer__ref" {...rest}>{children}</code>;
      }

      const isFocused = node.id === focusNodeId;
      const accent = getNodeBaseColor(node.color);

      return (
        <button
          type="button"
          className={`ask-dewey-chip${isFocused ? ' ask-dewey-chip--focused' : ''}`}
          style={{ borderColor: accent, ...(isFocused ? { backgroundColor: accent } : {}) }}
          onClick={() => onFocusNode(node)}
          aria-pressed={isFocused}
          aria-label={`Focus ${node.name || node.id}${node.type ? ` (${node.type})` : ''}`}
          title={node.description || node.name || node.id}
        >
          <span className="ask-dewey-chip__dot" style={{ backgroundColor: accent }} aria-hidden="true" />
          {node.name || node.id}
        </button>
      );
    },
    [index, onFocusNode, focusNodeId],
  );

  const renderMarkdown = (source: string): React.ReactElement => (
    <Markdown remarkPlugins={[remarkGfm]} components={{ code: renderCode }}>
      {removeDuplicateEntityLabels(source, index)}
    </Markdown>
  );

  // No summary section: show the answer in full rather than promoting an
  // arbitrary fragment or hiding content the user cannot get back.
  if (!split) {
    return <div className="ask-dewey-answer">{renderMarkdown(answer)}</div>;
  }

  return (
    <div className="ask-dewey-answer">
      <div className="ask-dewey-answer__summary">{renderMarkdown(split.summary)}</div>

      {drift.length > 0 && (
        <div className="ask-dewey-answer__drift" role="note">
          <strong>Heads up:</strong> the summary references{' '}
          {drift.map((token, i) => (
            <React.Fragment key={token}>
              {i > 0 && ', '}
              <code>{token}</code>
            </React.Fragment>
          ))}{' '}
          {drift.length === 1 ? 'which was' : 'which were'} not established in the detailed
          answer.
        </div>
      )}

      {split.detail !== '' && (
        <div className="ask-dewey-answer__detail">
          <button
            type="button"
            className="ask-dewey-answer__disclosure"
            onClick={() => setDetailOpen((open) => !open)}
            aria-expanded={detailOpen}
            aria-controls="ask-dewey-detail"
          >
            <span
              className={`ask-dewey-answer__caret${detailOpen ? ' ask-dewey-answer__caret--open' : ''}`}
              aria-hidden="true"
            >
              ▸
            </span>
            {detailOpen ? 'Hide details' : 'Show details'}
          </button>
          <div id="ask-dewey-detail" hidden={!detailOpen}>
            {renderMarkdown(split.detail)}
          </div>
        </div>
      )}
    </div>
  );
};

export default AskDeweyAnswer;
